import { m } from "#/paraglide/messages";
import {
  Badge,
  Checkbox,
  CheckboxGroup,
  createDataTableColumnHelper,
  DataTable,
  DatePicker,
  Label,
  SearchField,
  Switch,
} from "@beweegbaas/ui";
import { parseDate, type DateValue } from "@internationalized/date";
import { createFileRoute, Link, stripSearchParams, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { toast } from "@beweegbaas/ui";
import { DataTableActions } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import {
  NOTITIES_SEARCH_DEFAULTS,
  notitiesSearchSchema,
  isNotitieType,
} from "#/modules/student/schema.search.notities";
import {
  getStudentNotities,
  submitDeleteNotitie,
} from "#/modules/school.students/students.functions";
import { useDebouncedSearchCommit } from "#/modules/util.helpers/use-debounced-search-commit";
import { getNotitieTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import type { components } from "#/schema";

const NotitieTypeValues: components["schemas"]["NotitieType"][] = [
  "Beweegresultaat",
  "SociaalEmotioneelThemaResultaat",
  "Administratie",
  "Rapport",
  "Les",
  "Leerling",
];

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/notities",
)({
  validateSearch: notitiesSearchSchema,
  // Keeps default filter/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(NOTITIES_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the server fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    searchTerm: search.searchTerm,
    from: search.from,
    to: search.to,
    afgerondAtFrom: search.afgerondAtFrom,
    afgerondAtTo: search.afgerondAtTo,
    notitieTypes: search.notitieTypes,
    includeFullHistory: search.includeFullHistory,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: ({ deps, params }) => getStudentNotities({ data: { leerlingId: params.id, ...deps } }),
  component: Notities,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} />,
});

type NotitieRow = Awaited<ReturnType<typeof getStudentNotities>>["notities"][number];

const helper = createDataTableColumnHelper<NotitieRow>();

function Notities() {
  const { id: leerlingId } = Route.useParams();
  const router = useRouter();
  const { notities, count, totalPages } = Route.useLoaderData();
  // Single source for both the sidebar filter controls and the table.
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const dateFormatter = useDateFormatter({ year: "2-digit", month: "short", day: "numeric" });

  let fromDateValue: DateValue;
  try {
    fromDateValue = parseDate(search.afgerondAtFrom);
  } catch {
    fromDateValue = parseDate(NOTITIES_SEARCH_DEFAULTS.afgerondAtFrom);
  }

  let toDateValue: DateValue;
  try {
    toDateValue = parseDate(search.afgerondAtTo);
  } catch {
    toDateValue = parseDate(NOTITIES_SEARCH_DEFAULTS.afgerondAtTo);
  }

  const selectedNotitieTypes = search.notitieTypes;

  // Text input: local state + debounced commit (no per-keystroke navigation).
  const [searchTermInput, setSearchTermInput] = useDebouncedSearchCommit(
    search.searchTerm,
    (value) =>
      navigate({
        search: (prev) => ({ ...prev, searchTerm: value, page: 1 }),
        replace: true,
        resetScroll: false,
      }),
    300,
  );

  const columns = useMemo(
    () =>
      helper.columns([
        helper.display({
          id: "datum",
          header: "Datum",
          meta: { cellClassName: "py-3 align-top" },
          cell: ({ row }) =>
            row.original.afgerondAt ? dateFormatter.format(new Date(row.original.afgerondAt)) : "-",
        }),
        helper.display({
          id: "type",
          header: "Type",
          meta: { cellClassName: "py-3 align-top" },
          cell: ({ row }) => (
            <Badge variant="secondary">{getNotitieTypeTranslation(row.original.notitieType)}</Badge>
          ),
        }),
        helper.display({
          id: "notitie",
          header: "Notitie",
          meta: { cellClassName: "py-3 align-top" },
          cell: ({ row }) => (
            // The app-wide overflow-wrap:anywhere lets the table shrink this column to a few
            // characters; only break words that are longer than the column itself.
            <div className="max-w-md min-w-40 text-pretty whitespace-pre-wrap [overflow-wrap:break-word]">
              {row.original.notitie || "-"}
            </div>
          ),
        }),
        helper.display({
          id: "actions",
          header: () => null,
          meta: { action: true, headerClassName: "w-0", cellClassName: "w-0 py-3 align-top" },
          cell: ({ row }) => {
            const { lesId } = row.original;
            return (
              // The icon buttons bleed into the cell padding so they sit on the row's first text line.
              <div className="-my-2">
                <DataTableActions
                  actions={[
                    ...(lesId
                      ? [
                          {
                            key: "les",
                            label: m["student.results.historie.view_les"](),
                            icon: <IconMaterialSymbolsVisibilityOutline className="size-4" />,
                            render: (
                              <Link
                                from={Route.fullPath}
                                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
                                params={(prev: Record<string, string>) => ({
                                  ...prev,
                                  lesson: lesId,
                                })}
                              />
                            ),
                          },
                        ]
                      : []),
                    {
                      key: "delete",
                      label: m["school.students.notities.delete_label"](),
                      icon: <IconMaterialSymbolsDeleteOutline className="size-4" />,
                      destructive: true,
                      confirm: {
                        title: m["school.students.notities.delete_confirm_title"](),
                        message: m["school.students.notities.delete_confirm"](),
                      },
                      onClick: async () => {
                        await submitDeleteNotitie({
                          data: {
                            id: row.original.id,
                            notitieType: row.original.notitieType,
                            leerlingId: row.original.leerlingId ?? leerlingId,
                          },
                        });
                        toast.success(m["school.students.notities.delete_success"]());
                        await router.invalidate();
                      },
                    },
                  ]}
                />
              </div>
            );
          },
        }),
      ]),
    [dateFormatter, leerlingId, router],
  );

  return (
    <Page.SidebarLayout>
      <Page.Sidebar>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <SearchField
              className={"w-auto grow"}
              value={searchTermInput}
              onValueChange={setSearchTermInput}
            />
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <DatePicker
              label="Van"
              value={fromDateValue}
              onChange={(date) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    afgerondAtFrom: date?.toString() ?? NOTITIES_SEARCH_DEFAULTS.afgerondAtFrom,
                    page: 1,
                  }),
                  replace: true,
                  resetScroll: false,
                })
              }
              fieldGroupClassName="min-w-auto grow"
              className={"grow"}
            />

            <DatePicker
              label="Tot"
              value={toDateValue}
              onChange={(date) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    afgerondAtTo: date?.toString() ?? NOTITIES_SEARCH_DEFAULTS.afgerondAtTo,
                    page: 1,
                  }),
                  replace: true,
                  resetScroll: false,
                })
              }
              fieldGroupClassName="min-w-auto grow"
              className={"grow"}
            />
          </div>

          {/* Without this the backend limits results to the current schooljaar, regardless of the date range. */}
          <div className="flex items-center gap-2">
            <Switch
              id="includeFullHistory"
              size="sm"
              checked={search.includeFullHistory}
              onCheckedChange={(checked) =>
                navigate({
                  search: (prev) => ({ ...prev, includeFullHistory: checked, page: 1 }),
                  replace: true,
                  resetScroll: false,
                })
              }
            />
            <Label htmlFor="includeFullHistory" className="cursor-pointer text-sm">
              {m["student.dashboard.notes.includeFullHistory"]()}
            </Label>
          </div>

          <div className="flex flex-col gap-2">
            <strong className="text-muted-foreground text-xs uppercase">
              {m["common.type"]()}
            </strong>
            <CheckboxGroup
              className={`bg-muted squircle rounded p-4
                text-sm supports-squircle:rounded-xl`}
              value={selectedNotitieTypes}
              onValueChange={(values) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    notitieTypes: values.filter(isNotitieType),
                    page: 1,
                  }),
                  replace: true,
                  resetScroll: false,
                })
              }
            >
              {NotitieTypeValues.map((type) => (
                <label key={type} className="flex cursor-pointer items-center gap-2">
                  <Checkbox value={type} />
                  <span>{getNotitieTypeTranslation(type)}</span>
                </label>
              ))}
            </CheckboxGroup>
          </div>
        </div>
      </Page.Sidebar>
      <PendingContent className="grow">
        <DataTable
          card
          aria-label="Notities"
          columns={columns}
          data={notities}
          getRowId={(notitie) => notitie.id}
          empty={<p className="w-full text-center">Geen notities gevonden</p>}
        />

        <Pager
          totalPages={totalPages}
          totalCount={count}
          className="border-0"
          page={search.page}
          onPageChange={(nextPage) =>
            navigate({ search: (prev) => ({ ...prev, page: nextPage }), replace: true })
          }
        />
      </PendingContent>
    </Page.SidebarLayout>
  );
}
